/**
 * <pack-size-options> — pack-size pill selector for product cards.
 *
 * Pack sizes are sibling products, not variants, so there is no variant picker
 * to hook into. Clicking a pill rewrites the surrounding card in place:
 * price, image, add-to-cart variant and the card's links all point at the
 * selected pack product. Nothing navigates.
 *
 * Markup contract — see snippets/pack-size-options.liquid.
 */

const CARD_ROOT_SELECTOR = [
  '.search-empty-carousel__card',
  '.plp-collection-grid__item',
  '.product-grid__item',
  '.bsf__card',
  'product-card',
].join(', ');

const CARD_LINK_SELECTOR = [
  'a.product-card__link',
  'a.plp-card-image',
  'a[ref="productTitleLink"]',
  '.search-empty-card__title a',
  'a.bsf__image-link',
  'a.bsf__title-link',
].join(', ');

class PackSizeOptions extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #handleClick = (event) => {
    const pill = event.target.closest('[data-pack-pill]');
    if (!pill || !this.contains(pill)) return;

    event.preventDefault();
    event.stopPropagation();

    if (pill.classList.contains(this.#activeClass)) return;

    this.#setActive(pill);
    this.#applyToCard(pill);
  };

  /** The pill BEM class is derived from the base class the snippet was rendered with. */
  get #activeClass() {
    const base = this.classList[0] || 'plp-card-options';
    return `${base}__pill--active`;
  }

  #setActive(pill) {
    const activeClass = this.#activeClass;
    this.querySelectorAll('[data-pack-pill]').forEach((item) => {
      const isActive = item === pill;
      item.classList.toggle(activeClass, isActive);
      item.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  #applyToCard(pill) {
    const root = this.closest(CARD_ROOT_SELECTOR) || this.parentElement;
    if (!root) return;

    const { productId, variantId, available, url, price, comparePrice, image } = pill.dataset;

    this.#updateAddToCart(root, { productId, variantId, available });
    this.#updateLinks(root, url);
    this.#updateImage(root, image);
    this.#updatePrice(root, price, comparePrice);

    this.dataset.currentProductId = productId || '';
  }

  #updateAddToCart(root, { productId, variantId, available }) {
    root.querySelectorAll('product-card-badge-add').forEach((badge) => {
      if (variantId) badge.dataset.variantId = variantId;
      if (productId) badge.dataset.productId = productId;
      badge.dataset.available = available === 'true' ? 'true' : 'false';
    });
  }

  #updateLinks(root, url) {
    if (!url) return;
    root.querySelectorAll(CARD_LINK_SELECTOR).forEach((link) => {
      link.setAttribute('href', url);
    });
  }

  #updateImage(root, image) {
    if (!image) return;
    const img = root.querySelector('.plp-card-image img, .search-empty-card__image img, .bsf__image-wrap img');
    if (!img) return;
    // srcset would win over the new src, so drop it and let the single URL apply.
    img.removeAttribute('srcset');
    img.removeAttribute('sizes');
    img.src = image;
  }

  #updatePrice(root, price, comparePrice) {
    if (!price) return;

    const flatPrice = root.querySelector('.search-empty-card__price, .bsf__price-amount');
    if (flatPrice) flatPrice.textContent = price;

    // Block-rendered card: <product-price> with separate regular/sale groups.
    const priceBlock = root.querySelector('product-price');
    if (!priceBlock) return;

    const regularGroup = priceBlock.querySelector('.price__regular');
    const saleGroup = priceBlock.querySelector('.price__sale');
    const onSale = Boolean(comparePrice);

    if (regularGroup) {
      const node = regularGroup.querySelector('.price');
      if (node) node.textContent = price;
      regularGroup.classList.toggle('price__hidden', onSale);
    }

    if (saleGroup) {
      const saleNode = saleGroup.querySelector('.price-item--sale');
      const compareNode = saleGroup.querySelector('.price-item--regular');
      if (saleNode) saleNode.textContent = price;
      if (compareNode) compareNode.textContent = comparePrice || '';
      saleGroup.classList.toggle('price__hidden', !onSale);
    }
  }
}

if (!customElements.get('pack-size-options')) {
  customElements.define('pack-size-options', PackSizeOptions);
}
